"use client";

import { useMemo, useState } from "react";
import { CreatorCard } from "@/components/CreatorCard";
import { filterCreators, uniqueRegions } from "@/lib/filterCreators";
import type { Creator, FishHabitat } from "@/lib/types";

const HABITATS: { id: FishHabitat | "all"; label: string }[] = [
  { id: "all", label: "All habitats" },
  { id: "reef", label: "Reef" },
  { id: "freshwater", label: "Freshwater" },
  { id: "deep-sea", label: "Deep sea" },
  { id: "coastal", label: "Coastal" },
];

type Props = { creators: Creator[] };

export function ExploreView({ creators: allCreators }: Props) {
  const [habitat, setHabitat] = useState<FishHabitat | "all">("all");
  const [region, setRegion] = useState<string>("all");
  const [query, setQuery] = useState("");

  const regions = useMemo(() => uniqueRegions(allCreators), [allCreators]);

  const filtered = useMemo(
    () => filterCreators(allCreators, { habitat, region, query }),
    [allCreators, habitat, region, query],
  );

  return (
    <>
      <div className="mt-8 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium uppercase tracking-wide text-teal-200/70">
            Habitat
          </p>
          <div
            className="mt-2 flex flex-wrap gap-2"
            role="group"
            aria-label="Filter by habitat"
          >
            {HABITATS.map((h) => {
              const active = habitat === h.id;
              return (
                <button
                  key={h.id}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setHabitat(h.id)}
                  className={
                    active
                      ? "rounded-full border border-teal-400/60 bg-teal-400/25 px-4 py-2 text-sm font-medium text-white shadow-sm"
                      : "rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm text-teal-100 transition hover:border-teal-400/50 hover:bg-white/10"
                  }
                >
                  {h.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-end lg:w-auto lg:min-w-[280px]">
          <div className="min-w-0 flex-1">
            <label
              htmlFor="explore-region"
              className="text-xs font-medium uppercase tracking-wide text-teal-200/70"
            >
              Region
            </label>
            <select
              id="explore-region"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className="mt-2 w-full rounded-xl border border-white/15 bg-[#042f2e]/90 px-3 py-2.5 text-sm text-white focus:border-teal-400/50 focus:outline-none"
            >
              <option value="all">All regions</option>
              {regions.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <label
          htmlFor="explore-search"
          className="text-xs font-medium uppercase tracking-wide text-teal-200/70"
        >
          Search
        </label>
        <input
          id="explore-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Name, bio, role, region…"
          className="mt-2 w-full max-w-xl rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-white placeholder:text-teal-200/40 focus:border-teal-400/50 focus:outline-none"
          autoComplete="off"
        />
      </div>

      <p className="mt-6 text-sm text-teal-200/70" aria-live="polite">
        {filtered.length === allCreators.length ? (
          <>
            Showing all{" "}
            <span className="tabular-nums text-teal-100">
              {allCreators.length}
            </span>{" "}
            creators
          </>
        ) : (
          <>
            <span className="tabular-nums text-teal-100">{filtered.length}</span>
            {" of "}
            <span className="tabular-nums">{allCreators.length}</span> creators
            match
          </>
        )}
      </p>

      {filtered.length === 0 ? (
        <div className="mt-10 rounded-2xl border border-white/10 bg-black/20 px-6 py-12 text-center">
          <p className="text-lg text-white">No captains match</p>
          <p className="mt-2 text-sm text-teal-200/75">
            Try another habitat, region, or search term.
          </p>
          <button
            type="button"
            onClick={() => {
              setHabitat("all");
              setRegion("all");
              setQuery("");
            }}
            className="mt-6 rounded-xl border border-white/20 px-4 py-2 text-sm text-teal-100 hover:bg-white/10"
          >
            Clear filters
          </button>
        </div>
      ) : (
        <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((c) => (
            <CreatorCard key={c.id} creator={c} />
          ))}
        </div>
      )}
    </>
  );
}
